import * as tf from '@tensorflow/tfjs'
import { get_classmap } from './mapper'

const MODEL_FILE_URL = `/webapp/camera/stripped_ssd_softmax_web_model/model.json`
const INPUT_NODE_NAME = 'Preprocessor/sub'
const OUTPUT_NODE_NAME = ['concat', 'concat_1']
const CLASS_MAP = get_classmap()

Array.prototype.end = function () {
    return this[this.length - 1]
}

export class SSD {
    constructor() {
        this.input_shape = [1, 256, 256, 3]
        this.class_n = 2 // include background
        this.final_activation = 'softmax'

        // PW_SSD_V2
        this.feature_map = {
            'FeatureExtractor/Conv_2/Relu:0': [2, 32, 32, 64],
            'FeatureExtractor/Conv_4/Relu:0': [2, 16, 16, 64],
            'FeatureExtractor/Conv_6/Relu:0': [2, 8, 8, 128],
        }

        this.aspect_ratio = [1, 2, 0.5, 3, 1 / 3] // wont be used in PW_SSD_V2
        this.n_boxs = [2, 3, 8] // already plus the additional box crorepone to ratio 1.
        // this.n_boxs = [3, 6, 6, 6, 6, 6]
        this.scale_factor = {
            y_scale: 10.0,
            x_scale: 10.0,
            height_scale: 5.0,
            width_scale: 5.0,
        }

        this.predict_layers = Object.values(this.feature_map).length
        this.max_scale = 0.95
        this.min_scale = 0.2
        this.threshold = 0.4

        // this.anchor_priors = this._get_anchor()
        this.anchor_priors = this._get_pw_anchor()
        this.decode_params = {}

        this.log_level = 1
    }

    async load() {
        this.model = await tf.loadGraphModel(MODEL_FILE_URL, {
            onProgress: (p) => {
                console.log('load_weights: ', p)
            },
        })
    }

    dispose() {
        if (this.model) {
            this.model.dispose()
        }
    }

    _log(str_array, level = 1) {}

    /**
     * Generate anchor boxes for each boxpredictor layer according to this.aspect_ratio, this.min_scale, this.max_scale, this.n_box
     *
     * @return list of anchor box widht, height
     * ex: [
     *  [{h: 0.5, w: 1}, {h: 0.33, w: 1}],
     *  [{h: 0.5, w: 1}, {h: 0.33, w: 1}],
     *  ...
     * ]
     */

    /**
     * Generate anchor boxes for each boxpredictor layer according to
     * alfred-models.git:pw_dev/research/object_detection/pipeline_config/coco2017/person_1_vehicle_2_cat_and_dog_3/my_feature_extractor_v2.config
     *
     * @return list of anchor box widht, height
     * ex: [
     *  [{h: 0.5, w: 1}, {h: 0.33, w: 1}],
     *  [{h: 0.5, w: 1}, {h: 0.33, w: 1}],
     *  ...
     * ]
     */
    _get_pw_anchor() {
        let scales = [
            [0.10825317547305482, 0.10825317547305482],
            [0.125, 0.1767766952966369, 0.1767766952966369],
            [
                0.375,
                0.34232659844072877,
                0.30618621784789724,
                0.30618621784789724,
                0.4841229182759271,
                0.4841229182759271,
                0.640625,
                0.75,
            ],
        ]
        let ratios = [
            [0.75, 1.3333333333333333],
            [1.0, 0.5, 2.0],
            [
                1.0,
                0.3,
                0.6666666666666666,
                1.5,
                2.4,
                0.4166666666666667,
                1.0,
                1.0,
            ],
        ]
        let anchors = []

        for (let i = 0; i < this.predict_layers; i++) {
            if (scales[i].length !== ratios[i].length)
                throw Error(
                    'Scale value should be paired with ratio value in PW model!'
                )
            let single_layer_anchor = []

            for (let b = 0; b < scales[i].length; b++) {
                let s = scales[i][b]
                let sqrt_r = Math.sqrt(ratios[i][b] + 0.000000001)
                single_layer_anchor.push({ h: s / sqrt_r, w: s * sqrt_r })
            }
            anchors.push(single_layer_anchor)
        }

        return anchors
    }

    /**
     * Convert network's raw float32 output to normalize box coordinate and size.
     *
     * @param class_pred tensor2d (batch_size, sum(layer_anchor_n) == number of bbox, class_num)
     * @param box_pred tensor2d (batch_size, sum(layer_anchor_n), 4)
     * @return list of normlimze_box object: [{ x: [0:1], y: [0:1], w: [0:1], h:[0,1], cls: array(class_num) }, ...]
     */
    _decode(class_pred, box_pred) {
        if (this.final_activation == 'softmax')
            class_pred = class_pred.softmax(-1)
        else if (this.final_activation == 'softmax')
            class_pred = class_pred.sigmoid(-1)
        else throw Error(`Unknow activation function: ${this.final_activation}`)

        let featmap_shape = Object.values(this.feature_map)
        let layer_anchor_n = featmap_shape.map(
            (e, i, a) => e[1] * e[2] * this.n_boxs[i]
        )

        let config_box_n = layer_anchor_n.reduce((sum, e) => sum + e)
        let model_output_n = class_pred.shape[1]

        if (config_box_n != model_output_n)
            throw Error(
                `You should have same number of anchor bo as prediction's last dim (${config_box_n} != ${model_output_n})`
            )
        // console.log('layer_anchor_n:', layer_anchor_n)

        const which_layer = (output_id) => {
            if (output_id > box_pred.shape[1])
                throw Error(
                    `anchor id shold be smaller than number of total anchor(${box_pred.shape[1]})`
                )

            let marker = 0
            for (var i = 0; i < layer_anchor_n.length; i++) {
                marker += layer_anchor_n[i]

                if (output_id >= marker) continue
                else return i - 1
            }
        }

        let boxes = []
        let anchor_priors = this.anchor_priors

        let box_pred_offset = [] // predict_layer_num * (h, w, n_boxes, 4)
        let box_pred_mask = [] // predict_layer_num * (h, w, n_boxes, 1)
        let box_pred_cls = [] // predict_layer_num * (h, w, n_boxes, n_class)

        for (let batch = 0; batch < 1; batch++) {
            let marker = 0
            for (let i = 0; i < layer_anchor_n.length; i++) {
                let wh = featmap_shape[i][1]
                let range = tf.linspace(1 / wh / 2, wh - 1 / wh / 2, wh)
                range = tf.div(range, tf.scalar(wh))

                let anchors = []
                for (let b = 0; b < this.n_boxs[i]; b++) {
                    let anchor = anchor_priors[i][b]
                    // console.log('anochors', this.n_boxs[i], anchor)
                    anchors.push([anchor.w, anchor.h])
                }

                let layer_predict = box_pred
                    .slice([batch, marker, 0], [1, layer_anchor_n[i], 4])
                    .reshape([wh, wh, this.n_boxs[i], 4])

                let layer_cls = class_pred
                    .slice([batch, marker, 1], [1, layer_anchor_n[i], -1])
                    .reshape([wh, wh, this.n_boxs[i], this.class_n - 1])
                let layer_cls_mask = layer_cls
                    .max(-1)
                    .greater(tf.scalar(this.threshold))

                let y_grid = layer_predict
                    .slice([0, 0, 0, 0], [-1, -1, -1, 1])
                    .div(tf.scalar(this.scale_factor.y_scale))
                y_grid = y_grid.mul(
                    tf.tensor1d(anchors.map((e) => e[0])).reshape([1, 1, -1, 1])
                )
                y_grid = tf.add(y_grid, range.reshape([wh, 1, 1, 1]))

                let x_grid = layer_predict
                    .slice([0, 0, 0, 1], [-1, -1, -1, 1])
                    .div(tf.scalar(this.scale_factor.x_scale))
                x_grid = x_grid.mul(
                    tf.tensor1d(anchors.map((e) => e[1])).reshape([1, 1, -1, 1])
                )
                x_grid = tf.add(x_grid, range.reshape([1, wh, 1, 1]))

                let hw_grid = layer_predict
                    .slice([0, 0, 0, 2], [-1, -1, -1, 2])
                    .div(
                        tf
                            .tensor1d([
                                this.scale_factor.height_scale,
                                this.scale_factor.width_scale,
                            ])
                            .reshape([1, 1, 1, 2])
                    )
                    .exp()

                hw_grid = tf.mul(
                    hw_grid,
                    tf.tensor2d(anchors).reshape([1, 1, this.n_boxs[i], 2])
                )

                // console.log(`x_grid: ${x_grid.shape}, y_grid: ${y_grid.shape}, hw: ${hw_grid.shape}`)
                box_pred_offset.push(tf.concat([y_grid, x_grid, hw_grid], -1))
                box_pred_mask.push(layer_cls_mask)
                box_pred_cls.push(layer_cls)
                marker += layer_anchor_n[i]
            }
        }

        let output_boxes = []

        for (let n = 0; n < box_pred_mask.length; n++) {
            let mask = box_pred_mask[n].dataSync()
            let grid = box_pred_offset[n].dataSync()
            let softmax_cls = box_pred_cls[n].dataSync()

            if (mask.length !== layer_anchor_n[n])
                throw Error('Sould have same number of mask as prior_anchor')
            if (mask.length * 4 !== grid.length)
                throw Error(
                    `mask len * 4 ${mask.length * 4} != grid len ${grid.length}`
                )

            for (let m = 0; m < mask.length; m++) {
                if (mask[m] > 0) {
                    let classes_v = softmax_cls.slice(
                        m * (this.class_n - 1),
                        (m + 1) * (this.class_n - 1)
                    )

                    output_boxes.push({
                        y: grid[m * 4],
                        x: grid[m * 4 + 1],
                        h: grid[m * 4 + 2],
                        w: grid[m * 4 + 3],
                        cls: classes_v,
                        cls_id: classes_v.indexOf(Math.max(...classes_v)),
                    })
                }
            }
        }

        return output_boxes
    }

    _decode_fast(class_pred, box_pred) {
        return tf.tidy(() => {
            class_pred = class_pred.sigmoid(-1).reshape([-1, this.class_n])
            box_pred = box_pred.reshape([-1, 4])

            let featmap_shape = Object.values(this.feature_map)
            let layer_anchor_n = featmap_shape.map(
                (e, i, a) => e[1] * e[2] * this.n_boxs[i]
            )

            let config_box_n = layer_anchor_n.reduce((sum, e) => sum + e)
            let model_output_n = class_pred.shape[0]

            if (config_box_n != model_output_n)
                throw Error(
                    `You should have same number of anchor bo as prediction's last dim (${config_box_n} != ${model_output_n})`
                )

            const which_layer = (output_id) => {
                if (output_id > box_pred.shape[0])
                    throw Error(
                        `anchor id shold be smaller than number of total anchor(${box_pred.shape[0]})`
                    )

                let marker = 0
                for (let i = 0; i < layer_anchor_n.length; i++) {
                    let marker_ = marker
                    marker += layer_anchor_n[i]

                    if (output_id >= marker) continue
                    else return [i, marker_]
                }
            }

            const layer_grid1d = () => {
                let grid_offset_list = []

                for (let i = 0; i < layer_anchor_n.length; i++) {
                    let wh = featmap_shape[i][1]
                    let range = {
                        start: 1 / 2,
                        end: wh - 1 / 2,
                        step: 1,
                    }
                    let linespace = []

                    for (let j = range.start; j <= range.end; j += range.step) {
                        linespace.push(j / wh)
                    }
                    grid_offset_list.push(linespace)
                }

                return grid_offset_list
            }

            const id2xy = (layer, offset, id) => {
                let local_offest = id - offset
                let grid1d_offest = Math.floor(
                    local_offest / this.n_boxs[layer]
                )
                let y = Math.floor(grid1d_offest / featmap_shape[layer][1])
                let x = grid1d_offest % featmap_shape[layer][1]
                return { x: x, y: y }
            }

            if (this.decode_params.anchor_center === undefined)
                this.decode_params.anchor_center = layer_grid1d()

            let boxes = []
            let anchor_priors = this.anchor_priors
            let anchor_center = this.decode_params.anchor_center

            let cls_without_bg = class_pred.slice([0, 1], [-1, -1])
            let box_pred_mask = cls_without_bg
                .max(-1)
                .greater(tf.scalar(this.threshold))
                .dataSync()
            let mask_indies = []
            let output_boxes = []

            for (let m = 0; m < box_pred_mask.length; m++) {
                if (box_pred_mask[m] > 0) {
                    mask_indies.push(m)
                }
            }

            if (mask_indies.length > 0) {
                let classes_gather = cls_without_bg
                    .gather(tf.tensor1d(mask_indies, 'int32'))
                    .dataSync()
                let box_gather = box_pred
                    .gather(tf.tensor1d(mask_indies, 'int32'))
                    .dataSync()

                for (let m of mask_indies) {
                    let I = mask_indies.indexOf(m)

                    let lag = which_layer(m)
                    let L = lag[0]
                    let grid_offset = lag[1]

                    let crood = id2xy(L, grid_offset, m)
                    let anchor =
                        anchor_priors[L][(m - grid_offset) % this.n_boxs[L]]

                    let classes_v = classes_gather.slice(
                        I * (this.class_n - 1),
                        (I + 1) * (this.class_n - 1)
                    )
                    let box = box_gather.slice(I * 4, (I + 1) * 4)

                    box[0] /= this.scale_factor.y_scale
                    box[0] *= anchor.h
                    box[0] += anchor_center[L][crood.y]

                    box[1] /= this.scale_factor.x_scale
                    box[1] *= anchor.w
                    box[1] += anchor_center[L][crood.x]

                    box[2] /= this.scale_factor.height_scale
                    box[2] = Math.exp(box[2])
                    box[2] *= anchor.h

                    box[3] /= this.scale_factor.width_scale
                    box[3] = Math.exp(box[3])
                    box[3] *= anchor.w

                    output_boxes.push({
                        y: box[0],
                        x: box[1],
                        h: box[2],
                        w: box[3],
                        cls: classes_v,
                        cls_id: classes_v.indexOf(Math.max(...classes_v)),
                    })
                }
            }

            return output_boxes
        })
    }

    _apply_box_imgsize(norm_box, img_size = null) {
        let true_boxes = []
        let img_height = img_size === null ? this.input_shape[1] : img_size.h
        let img_width = img_size === null ? this.input_shape[2] : img_size.w

        const clip_value = (V) => {
            if (V > img_width) return img_width
            else if (V < 0) return 0
            else return Number.parseInt(V)
        }

        for (let nbox of norm_box) {
            let top_cls = nbox.cls.indexOf(Math.max(...nbox.cls))
            if (CLASS_MAP[top_cls] === undefined) continue

            true_boxes.push({
                x_min: clip_value((nbox.x - nbox.w / 2) * img_width),
                x_max: clip_value((nbox.x + nbox.w / 2) * img_width),
                y_min: clip_value((nbox.y - nbox.h / 2) * img_height),
                y_max: clip_value((nbox.y + nbox.h / 2) * img_height),
                cls: CLASS_MAP[top_cls],
                cls_id: top_cls,
                cls_score: Math.max(...nbox.cls),
            })
        }

        return true_boxes
    }

    /****************************************************************
     * NonMaxSuppression
     *
     * @param boxes list of boxes object [{
     *   x: [0:img_width], y: [0:img_height], w: [0:img_width], h:[0,img_height],
     *   cls: class name, cls_score: [0:1] }, ...]
     * @return boxes from original list that don't overlap each other.
     ****************************************************************/
    _NMS(boxes, threshold) {
        function get_box_area(box) {
            let w = box.x_max - box.x_min
            let h = box.y_max - box.y_min
            return w * h
        }

        let box_by_cls = {}

        // group by object class
        for (let box of boxes) {
            if (box_by_cls[box.cls] === undefined) box_by_cls[box.cls] = [box]
            else box_by_cls[box.cls].push(box)
        }

        let picked_box = []

        for (let cls_name in box_by_cls) {
            box_by_cls[cls_name].sort((a, b) => b.cls_score - a.cls_score) // Descending order

            while (box_by_cls[cls_name].length > 0) {
                let base_box = box_by_cls[cls_name][0]
                let base_area = get_box_area(base_box)
                picked_box.push(base_box)

                box_by_cls[cls_name] = box_by_cls[cls_name].filter(
                    (box, i, a) => {
                        let box_area = get_box_area(box)
                        let intersect = {
                            x_max: Math.min(base_box.x_max, box.x_max),
                            x_min: Math.max(base_box.x_min, box.x_min),
                            y_max: Math.min(base_box.y_max, box.y_max),
                            y_min: Math.max(base_box.y_min, box.y_min),
                        }
                        intersect = get_box_area(intersect)

                        let IOU = intersect / ((base_area + box_area) / 2)
                        return IOU < threshold
                    }
                )
            }
        }

        return picked_box
    }

    /****************************************************************
     * Infer through MobileNet. This does standard ImageNet pre-processing before
     * inferring through the model. This method returns named activations as well
     * as softmax logits.
     *
     * @param input un-preprocessed input Array.
     * @return The softmax logits.
     */
    async predict(input) {
        if (this.model === undefined) {
            await this.load()
        }

        return tf.tidy(() => {
            const preprocessedInput = tf.sub(
                tf.div(input.asType('float32'), tf.scalar(255)),
                tf.scalar(0.5)
            )

            const reshapedInput = preprocessedInput.reshape([
                1,
                ...preprocessedInput.shape,
            ])
            return this.model.execute(
                { [INPUT_NODE_NAME]: reshapedInput },
                OUTPUT_NODE_NAME
            )
        })
    }

    /****************************************************************
     * Apply decode and other postpossessor to SSD's raw output.
     * Give you list of boxes with croodate of orginal image scaling as return.
     *
     * @param input un-preprocessed input Array.
     * @param topK int: keep K box according to box's confiend score.
     * @return array of boxes object
     */
    decode_predict(predictions, apply_nms, img_size = null) {
        // After tfjs 0.12.0, FroneModel no longer return object after predict!
        if (
            predictions.concat === undefined ||
            predictions.concat_1 === undefined
        ) {
            for (let I = 0; I < OUTPUT_NODE_NAME.length; I++)
                predictions[OUTPUT_NODE_NAME[I]] = predictions[I]
        }

        if (this.log_level >= 2) {
            const box_pred_values = predictions.concat.dataSync()
            const class_pred_values = predictions.concat_1.dataSync()
        }

        let boxes = this._decode_fast(predictions.concat_1, predictions.concat)
        // let boxes = this._decode(predictions.concat_1, predictions.concat)

        boxes = this._apply_box_imgsize(boxes, img_size)
        if (apply_nms) boxes = this._NMS(boxes, 0.4)

        predictions.concat.dispose()
        predictions.concat_1.dispose()
        return boxes
    }
}

export class FaceSSD {
    constructor(model_url) {
        this.INPUT_NODE_NAME = 'Preprocessor/sub'
        // decoded boxes, decoded keypoint, scores
        this.OUTPUT_NODE_NAME = [
            'Postprocessor/Reshape_4',
            'Postprocessor/Reshape_2',
            'Postprocessor/Slice',
        ]
        this.input_img_size = [384, 512]
        this.model_url = model_url
    }

    async load_weights() {
        // function sleep(ms) {
        //     return new Promise((resolve) => setTimeout(resolve, ms))
        // }
        // await sleep(3000)

        this.model = await tf.loadGraphModel(this.model_url, {
            onProgress: (p) => {
                console.log('load_weights: ', p)
            },
        })
        this.predict(tf.zeros([1, ...this.input_img_size, 3]))
    }

    preprocess(image_dom) {
        return tf.tidy(() => {
            let input_tensor =
                image_dom instanceof tf.Tensor
                    ? image_dom
                    : tf.browser.fromPixels(image_dom)
            input_tensor = tf.image.resizeBilinear(input_tensor, [384, 512])
            input_tensor = tf
                .cast(input_tensor, 'float32')
                .div(255)
                .expandDims(0)
            input_tensor = tf.sub(input_tensor, 0.5)
            return input_tensor
        })
    }

    predict(image) {
        return tf.tidy(() => {
            let t_0 = performance.now()
            let pred = this.model.execute(
                {
                    [this.INPUT_NODE_NAME]: image,
                },
                this.OUTPUT_NODE_NAME
            )
            console.log('execute: ', performance.now() - t_0)

            let t_1 = performance.now()
            const detect_boxes = pred[0].squeeze(0) // (1, 6144, 4)
            const detect_keypoints = pred[1].squeeze(0) // (1, 6144, 5, 2)
            const detect_scores = pred[2].squeeze(0).squeeze(-1) // (1, 6144, 1)

            const prevBackend = tf.getBackend()
            // // run post process in cpu
            tf.setBackend('cpu')
            const nms = tf.image.nonMaxSuppressionWithScore(
                detect_boxes,
                detect_scores,
                10,
                0.5,
                0.5
            )
            tf.setBackend(prevBackend)

            const final_boxes = tf
                .gather(detect_boxes, nms.selectedIndices)
                .dataSync()
            const final_kp = tf
                .gather(detect_keypoints, nms.selectedIndices)
                .dataSync()
            const final_score = nms.selectedScores.dataSync()
            let t_2 = performance.now()
            console.log('Download data: ', t_2 - t_1)

            let detects = []
            for (let i = 0; i < final_score.length; i++) {
                detects.push({
                    x_min: final_boxes[i * 4 + 1],
                    y_min: final_boxes[i * 4 + 0],
                    x_max: final_boxes[i * 4 + 3],
                    y_max: final_boxes[i * 4 + 2],
                    cls: 'face',
                    cls_score: final_score[i],
                    keypoints: final_kp.slice(i * 2 * 5, (i + 1) * 2 * 5),
                })
            }
            return detects
        })
    }

    multi_scale_predict(image) {
        return tf.tidy(() => {
            let t_0 = performance.now()
            const scales = [1, 0.5]

            let detect_boxes = []
            let detect_keypoints = []
            let detect_scores = []
            for (const scale of scales) {
                const new_size = [
                    Number.parseInt(this.input_img_size[0] * scale),
                    Number.parseInt(this.input_img_size[1] * scale),
                ]
                const pad_size = [
                    [0, 0],
                    [0, this.input_img_size[0] - new_size[0]],
                    [0, this.input_img_size[1] - new_size[1]],
                    [0, 0],
                ]
                let resize_img =
                    scale != 1
                        ? tf.image.resizeNearestNeighbor(image, new_size)
                        : image
                resize_img = tf.pad(resize_img, pad_size)

                let pred = this.model.execute(
                    {
                        [this.INPUT_NODE_NAME]: resize_img,
                    },
                    this.OUTPUT_NODE_NAME
                )

                let t_1 = performance.now()
                const det_boxes = pred[0].squeeze(0).mul(1 / scale) // (1, 6144, 4)
                const det_keypoints = pred[1].squeeze(0).mul(1 / scale) // (1, 6144, 5, 2)
                const det_scores = pred[2].squeeze(0).squeeze(-1) // (1, 6144, 1)

                detect_boxes.push(det_boxes)
                detect_keypoints.push(det_keypoints)
                detect_scores.push(det_scores)
            }
            console.log('execute: ', performance.now() - t_0)

            detect_boxes = tf.concat(detect_boxes, 0)
            detect_keypoints = tf.concat(detect_keypoints, 0)
            detect_scores = tf.concat(detect_scores, 0)

            const prevBackend = tf.getBackend()
            // // run post process in cpu
            tf.setBackend('cpu')
            const nms = tf.image.nonMaxSuppressionWithScore(
                detect_boxes,
                detect_scores,
                10,
                0.5,
                0.6
            )
            tf.setBackend(prevBackend)

            const final_boxes = tf
                .gather(detect_boxes, nms.selectedIndices)
                .dataSync()
            const final_kp = tf
                .gather(detect_keypoints, nms.selectedIndices)
                .dataSync()
            const final_score = nms.selectedScores.dataSync()
            let t_2 = performance.now()
            // console.log('Download data: ', t_2 - t_1)

            let detects = []
            for (let i = 0; i < final_score.length; i++) {
                detects.push({
                    x_min: final_boxes[i * 4 + 1],
                    y_min: final_boxes[i * 4 + 0],
                    x_max: final_boxes[i * 4 + 3],
                    y_max: final_boxes[i * 4 + 2],
                    cls: 'face',
                    cls_score: final_score[i],
                    keypoints: final_kp.slice(i * 2 * 5, (i + 1) * 2 * 5),
                })
            }
            return detects
        })
    }

    rescale_detect_crood(detects, target_img_size) {
        let new_det = []
        for (const det of detects) {
            new_det.push({
                x_min: Number.parseInt(
                    (det.x_min / this.input_img_size[1]) * target_img_size[1]
                ),
                y_min: Number.parseInt(
                    (det.y_min / this.input_img_size[0]) * target_img_size[0]
                ),
                x_max: Number.parseInt(
                    (det.x_max / this.input_img_size[1]) * target_img_size[1]
                ),
                y_max: Number.parseInt(
                    (det.y_max / this.input_img_size[0]) * target_img_size[0]
                ),
                cls: 'face',
                cls_score: det.cls_score,
                keypoints: det.keypoints.map((v, i, a) => {
                    if (i % 2 == 0)
                        return (v / this.input_img_size[0]) * target_img_size[0]
                    else
                        return (v / this.input_img_size[1]) * target_img_size[1]
                }),
            })
        }
        return new_det
    }
}
